import React, { useMemo, useState } from "react";
import { createAvatar } from "@dicebear/core";
import { identicon } from "@dicebear/collection";

import { relativeTime } from "../utils/post";

/**
 * One comment or one reply. Styling lives in src/styles/bleep-theme.css
 * (`.cmt*`).
 *
 * The shape is the one every threaded discussion has converged on: the words in
 * a filled bubble, the controls as plain text underneath it. The controls used
 * to be bordered buttons inside the bubble, which gave Reply and Delete the same
 * visual weight as the comment itself and left the destructive one sitting in a
 * `justify-content: space-between` row padded out with an empty `<span />`.
 *
 * Fancybox is bound once by the post page, not here. Every instance of this
 * component used to bind the shared `comment-gallery` selector on mount and call
 * the *global* `Fancybox.destroy()` on unmount, so deleting any one comment tore
 * the lightbox off every remaining comment and off the post's own photos.
 */
const Comment = ({
  comment,
  handleUpdateComments,
  postStatus,
  handleShowEmailAuthModal,
  userId,
  isReply,
  thread,
}) => {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [reply, setReply] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleReplyInput = () => {
    setShowReplyInput(!showReplyInput);
    setReply("");
  };

  // Prefer organization logo if present; else fall back to Dicebear identicon
  const avatarSrc = useMemo(() => {
    const logo = comment?.user?.organizationLogo;
    if (logo) return logo;
    if (!comment?.displayName) return null;
    return createAvatar(identicon, { seed: comment.displayName }).toDataUri();
  }, [comment?.user?.organizationLogo, comment?.displayName]);

  const formatExactDate = (dateTimeString) =>
    new Date(dateTimeString).toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });

  const handleDelete = (commentId) => {
    const url = isReply
      ? `/api/commentReplies/${commentId}`
      : `/api/comments/${commentId}`;

    fetch(url, { method: "DELETE" })
      .then((res) => {
        if (res.ok) {
          handleUpdateComments();
        } else {
          console.error("Error deleting comment:", res.statusText);
        }
      })
      .catch((error) => console.error("Fetch error:", error));
  };

  const postReply = (commentId, replyText) => {
    if (!userId) {
      handleShowEmailAuthModal();
      return;
    }
    if (!comment?.post || !comment?._id) {
      console.error("Invalid comment data.");
      return;
    }

    const raw = JSON.stringify({
      user: userId,
      post: comment.post,
      replyTo: comment.user._id,
      thread: thread || comment._id,
      comment: replyText,
    });

    fetch(`/api/commentReplies`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: raw,
    })
      .then((response) => {
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then(() => {
        handleUpdateComments();
        handleReplyInput();
      })
      .catch((error) => console.error("Error:", error));
  };

  // Both of these test the author's own fields rather than the presence of a
  // `user` object, because that object is not proof there is an author.
  // server/routes/comments.js builds it as `{ ...plain.user, organization }`,
  // and spreading a null user yields `{ organization: "Unknown Organization" }`:
  // truthy, but with no `_id` and no `type`.
  //
  // That turned two comparisons into `undefined === undefined`. A signed-out
  // reader has no `userId`, so every orphaned comment matched `isMine` and
  // offered a Delete button and a "You" tag; and `undefined !== "user"` made the
  // same comments render as staff, inverted bubble and all, badged "Unknown
  // Organization". Requiring the field to exist is what fixes both.
  const authorId = comment?.user?._id;
  const authorType = comment?.user?.type;
  const isStaff = Boolean(authorId && authorType && authorType !== "user");
  const isMine = Boolean(userId && authorId && authorId === userId);
  const canReply = postStatus !== "Resolved";
  const exactTime = formatExactDate(comment?.dateTime);

  // The old check read `comment.images` but mapped `comment.imageUrls`, so a
  // record carrying one without the other threw on render.
  const images = Array.isArray(comment?.imageUrls) ? comment.imageUrls : [];

  return (
    <div className={`cmt-thread${isReply ? " cmt-thread--reply" : ""}`}>
      <div className="cmt">
        <div className="cmt__avatar-wrap">
          <img
            src={avatarSrc || undefined}
            alt=""
            className={`cmt__avatar${
              comment?.user?.organizationLogo ? " cmt__avatar--photo" : ""
            }`}
          />
        </div>

        <div className="cmt__main">
          <div className={`cmt__bubble${isStaff ? " cmt__bubble--staff" : ""}`}>
            <div className="cmt__head">
              <span className="cmt__name">@{comment.displayName}</span>

              {/* Staff accounts are always attached to an organization, but the
                  field is not guaranteed to be populated, and a badge that says
                  nothing is worse than a neutral one. */}
              {isStaff && (
                <span className="cmt__badge">
                  <i className="fas fa-circle-check" aria-hidden="true" />
                  {comment?.user?.organization || "Official"}
                </span>
              )}

              {isMine && <span className="cmt__mine">You</span>}
            </div>

            {isReply && comment?.replyToDisplayName && (
              <p className="cmt__replying">
                <i className="fas fa-reply" aria-hidden="true" />
                Replying to @{comment.replyToDisplayName}
              </p>
            )}

            {comment.comment && <p className="cmt__text">{comment.comment}</p>}

            {images.length > 0 && (
              <div className="cmt__images">
                {images.map((image, index) => (
                  <a
                    key={image}
                    className="cmt__img-link"
                    data-fancybox="comment-gallery"
                    href={image}
                    aria-label={`Open attachment ${index + 1} of ${images.length}`}
                  >
                    <img src={image} alt="" className="cmt__img" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="cmt__meta">
            <time
              className="cmt__time"
              dateTime={comment?.dateTime}
              title={exactTime}
            >
              {relativeTime(comment?.dateTime)}
            </time>

            {canReply && (
              <button
                type="button"
                className="cmt__action"
                onClick={handleReplyInput}
                aria-expanded={showReplyInput}
              >
                Reply
              </button>
            )}

            {/* Deleting is immediate and irreversible, and on a phone this sits
                a thumb's width from Reply. Two taps rather than a modal: it
                keeps the confirmation where the action is. */}
            {isMine &&
              (confirmDelete ? (
                <span className="cmt__confirm">
                  Delete this?
                  <button
                    type="button"
                    className="cmt__action cmt__action--danger"
                    onClick={() => handleDelete(comment._id)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className="cmt__action"
                    onClick={() => setConfirmDelete(false)}
                  >
                    No
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="cmt__action"
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </button>
              ))}
          </div>

          {showReplyInput && (
            <div className="cmt__composer">
              <textarea
                className="cmt__textarea"
                autoFocus
                rows={3}
                onChange={(e) => setReply(e.target.value)}
                value={reply}
                placeholder={`Reply to @${comment.displayName}`}
                aria-label={`Reply to ${comment.displayName}`}
              />
              {/* The post page's own composer buttons, not a second pair. This
                  is the same action one nesting level down, so it should be the
                  same control, and their colours are already worked out against
                  this page: the previous local `btn--primary` put white on
                  --bl-brand-ink, which flips to light orange in the dark scope
                  and lands at 2.2:1. */}
              <div className="cmt__composer-actions">
                <button
                  type="button"
                  className="comment-cancel-btn"
                  onClick={handleReplyInput}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="comment-post-btn"
                  onClick={() => postReply(comment._id, reply)}
                  disabled={!reply.trim()}
                >
                  Reply
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Comment;
